import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import Spinner from '../components/ui/Spinner';
import apiClient from '../clients/apiClient';

interface FinderResult {
  email: string;
  confidence: number;
  source: string;
  source_label: string;
  domain: string;
  first_name: string;
  last_name: string;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const color =
    confidence >= 85 ? 'bg-g text-white' :
    confidence >= 70 ? 'bg-amber-100 text-amber-700 border border-amber-200' :
    confidence >= 55 ? 'bg-blue-50 text-blue-600 border border-blue-200' :
    'bg-bg text-hint border border-border';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {confidence}% confidence
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-xs font-medium text-g hover:underline transition-colors"
    >
      {copied ? (
        <>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

export default function EmailFinder() {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FinderResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<FinderResult[]>(() => {
    try {
      const saved = localStorage.getItem('extramile_email_finder_history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('extramile_email_finder_history', JSON.stringify(history));
    } catch {
      // Storage quota or disabled
    }
  }, [history]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !company.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const { data } = await apiClient.post<FinderResult>('/api/email-finder', {
        name: name.trim(),
        company: company.trim(),
      });
      setResult(data);
      setHistory(prev => [data, ...prev.slice(0, 9)]);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout showBack backLabel="Back to Dashboard" backTo="/dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[28px] sm:text-[34px] font-semibold leading-snug text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Email <em style={{ fontStyle: 'italic', color: 'var(--g)' }}>Finder</em>
          </h1>
          <p className="text-sm text-muted mt-1">
            Find and verify direct recruiter email addresses using corporate domain patterns and deliverability checks.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* ── Left Column: Search Form & Live Result (lg:col-span-7) ── */}
          <div className="lg:col-span-7 space-y-6">
            {/* Form */}
            <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-ink mb-4" style={{ fontFamily: 'Instrument Serif, serif' }}>
                Lookup Recruiter
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 mb-5">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[1.2px] text-hint font-semibold mb-1.5">
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John Smith"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      className="w-full rounded-input px-3.5 py-2.5 text-sm text-ink bg-card focus:outline-none placeholder:text-hint transition-colors"
                      style={{ border: '1px solid var(--border)' }}
                      onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                      onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[1.2px] text-hint font-semibold mb-1.5">
                      Company Name or Domain
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Stripe or stripe.com"
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                      className="w-full rounded-input px-3.5 py-2.5 text-sm text-ink bg-card focus:outline-none placeholder:text-hint transition-colors"
                      style={{ border: '1px solid var(--border)' }}
                      onFocus={e => (e.target.style.borderColor = 'var(--g)')}
                      onBlur={e => (e.target.style.borderColor = 'var(--border)')}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !name.trim() || !company.trim()}
                  className="w-full py-2.5 px-4 rounded-input text-sm font-semibold text-white disabled:opacity-50 transition-all shadow-sm active:scale-95"
                  style={{ backgroundColor: 'var(--g)' }}
                  onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = 'var(--g-text)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--g)'; }}
                >
                  {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Finding email…</span> : 'Find Verified Email'}
                </button>
              </form>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-card px-4 py-3.5 shadow-sm">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="bg-card border border-g-border rounded-card p-5 sm:p-6 shadow-sm ring-1 ring-g/20">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-xs text-hint font-medium mb-1">
                      {result.first_name} {result.last_name} · {result.domain}
                    </p>
                    <p className="text-lg sm:text-xl font-semibold text-ink font-mono break-all">{result.email}</p>
                  </div>
                  <CopyButton text={result.email} />
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
                  <ConfidenceBadge confidence={result.confidence} />
                  <span className="text-xs text-muted">{result.source_label}</span>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Column: Recent Lookups & Pro Tips (lg:col-span-5) ── */}
          <div className="lg:col-span-5 space-y-6">
            {/* Recent Lookups */}
            <div className="bg-card border border-border rounded-card overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[1.2px] text-hint font-semibold">Recent Lookups</p>
                {history.length > 0 && (
                  <span className="text-[11px] text-muted">{history.length} saved</span>
                )}
              </div>
              {history.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted">
                  No lookups yet. Search for a recruiter to see history here.
                </div>
              ) : (
                <div className="divide-y divide-border max-h-80 overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={`${h.email}-${i}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-bg transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-hint truncate">{h.first_name} {h.last_name} · {h.domain}</p>
                        <p className="text-xs sm:text-sm font-medium text-ink font-mono truncate">{h.email}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <ConfidenceBadge confidence={h.confidence} />
                        <CopyButton text={h.email} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Verification Heuristics Guide */}
            <div className="bg-card border border-border rounded-card p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <span className="text-g">✦</span> Deliverability & Heuristics
              </h3>
              <p className="text-xs text-muted leading-relaxed mb-3">
                Emails found with <strong className="text-ink font-semibold">85%+ confidence</strong> match verified MX server records and corporate naming conventions.
              </p>
              <div className="bg-bg rounded-lg p-3 text-[11px] text-hint space-y-1.5 border border-border">
                <div className="flex items-center justify-between">
                  <span>Standard pattern:</span>
                  <span className="font-mono text-ink">first.last@company.com</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Bounce protection:</span>
                  <span className="text-g font-semibold">Automatic refund</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
